'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import Icon from '@/components/ui/AppIcon';
import Image from 'next/image';

interface NavigationItem {
  label: string;
  href: string;
  icon: string;
  badge?: number;
}

interface NavigationSection {
  title: string;
  items: NavigationItem[];
}

interface HeaderProps {
  className?: string;
}

const NAVIGATION_DATA: NavigationSection[] = [
  {
    title: 'MAIN',
    items: [
      { label: 'Home', href: '/homepage', icon: 'HomeIcon' },
      { label: 'Overview', href: '/services-overview', icon: 'ChartBarIcon' },
      { label: 'Case Studies', href: '/case-studies-hub', icon: 'DocumentTextIcon', badge: 3 },
      { label: 'Pricing', href: '/pricing', icon: 'CurrencyEuroIcon' },
    ],
  },
  {
    title: 'PAGES',
    items: [
      { label: 'Blog', href: '/blog', icon: 'NewspaperIcon' },
      { label: 'FAQ', href: '/faq', icon: 'QuestionMarkCircleIcon' },
      { label: 'About Us', href: '/about-us', icon: 'UserGroupIcon' },
    ],
  },
  {
    title: 'ADMIN',
    items: [
      { label: 'Admin Dashboard', href: '/admin', icon: 'ShieldCheckIcon' },
      { label: 'Account Setup', href: '/contact', icon: 'Cog6ToothIcon' },
      { label: 'Contact', href: '/contact', icon: 'EnvelopeIcon', badge: 2 },
    ],
  },
];

const Header = ({ className = '' }: HeaderProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isHydrated, setIsHydrated] = useState(false);
  const pathname = usePathname();

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const toggleMenu = () => {
    setIsExpanded((prev) => !prev);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      toggleMenu();
    }
  };

  const menuWidth = isExpanded ? 'w-72' : 'w-20';
  const contentMargin = isExpanded ? 'ml-72' : 'ml-20';

  return (
    <>
      <aside
        className={`fixed top-0 left-0 h-screen z-50 transition-all duration-500 ${menuWidth} ${className}`}
        style={{
          background: 'rgba(15, 15, 20, 0.95)',
          backdropFilter: 'blur(24px)',
          borderRight: '1px solid rgba(255, 255, 255, 0.1)',
          boxShadow: '0 0 60px rgba(0, 0, 0, 0.8), inset 0 0 40px rgba(255, 255, 255, 0.02)',
        }}
      >
        <div className="flex flex-col h-full">
          {/* Logo Section with Toggle */}
          <div className="flex items-center justify-between px-5 py-6 border-b border-white/10">
            <Link href="/homepage" className="flex items-center space-x-3 group">
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-br from-[#FF6600] to-[#228B22] rounded-xl blur-xl opacity-60"></div>
                <div className="relative bg-transparent p-2 rounded-xl overflow-hidden">
                  <Image
                    src="/assets/images/Untitled-1769944217471.jpeg"
                    alt="AIM AGENCY Logo"
                    width={40}
                    height={40}
                    className="object-contain"
                  />
                </div>
              </div>
              {isExpanded && (
                <span className="text-lg font-bold text-white transition-opacity duration-300">
                  AIM AGENCY
                </span>
              )}
            </Link>
            
            {/* Toggle Button */}
            <button
              onClick={toggleMenu}
              onKeyDown={handleKeyDown}
              className="p-2 rounded-lg bg-white/10 hover:bg-white/20 transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-[#FF6600]"
              aria-label={isExpanded ? 'Collapse menu' : 'Expand menu'}
              aria-expanded={isExpanded}
            >
              <Icon name={isExpanded ? 'ChevronLeftIcon' : 'ChevronRightIcon'} size={20} className="text-white" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 overflow-y-auto py-6 px-3 space-y-8">
            {NAVIGATION_DATA.map((section) => (
              <div key={section.title}>
                {isExpanded && (
                  <div className="px-4 mb-3">
                    <h3 className="text-xs font-bold text-white/50 tracking-widest">{section.title}</h3>
                  </div>
                )}

                <div className="space-y-1">
                  {section.items.map((item) => {
                    const isActive = isHydrated && pathname === item.href;

                    return (
                      <Link
                        key={`${section.title}-${item.label}`}
                        href={item.href}
                        className="group relative block"
                      >
                        <div
                          className={`relative flex items-center ${isExpanded ? 'justify-between' : 'justify-center'} px-4 py-3.5 rounded-xl transition-all duration-300 ${
                            isActive ? 'text-white' : 'text-white/80 hover:text-white'
                          }`}
                          style={{
                            background: isActive ? 'rgba(255, 102, 0, 0.2)' : 'rgba(255, 255, 255, 0.05)',
                            border: isActive ? '1px solid rgba(255, 102, 0, 0.5)' : '1px solid rgba(192, 192, 192, 0.2)',
                          }}
                        >
                          <div className={`flex items-center ${isExpanded ? 'space-x-3' : 'justify-center'}`}>
                            <div
                              className={`p-2 rounded-lg transition-all duration-300 flex items-center justify-center ${
                                isActive ? 'bg-[#FF6600]/30 text-white' : 'bg-white/10 text-white/80 group-hover:bg-[#FF6600]/25'
                              }`}
                            >
                              <Icon name={item.icon as any} size={20} />
                            </div>
                            {isExpanded && <span className="font-semibold text-sm">{item.label}</span>}
                          </div>

                          {isExpanded && item.badge && (
                            <div
                              className="px-2 py-0.5 rounded-full text-xs font-bold text-white"
                              style={{
                                background: 'linear-gradient(135deg, #FF6600, #FF8833)',
                              }}
                            >
                              {item.badge}
                            </div>
                          )}

                          {isActive && (
                            <div
                              className="absolute right-0 top-1/2 -translate-y-1/2 w-1 h-3/4 bg-gradient-to-b from-[#FF6600] via-[#FF8833] to-[#FF6600] rounded-l-full"
                            />
                          )}
                        </div>
                      </Link>
                    );
                  })}
                </div>
              </div>
            ))}
          </nav>

          {/* CTA Buttons */}
          <div className="px-3 py-6 border-t border-white/10 space-y-3">
            {isExpanded ? (
              <>
                <Link
                  href="/contact"
                  className="block w-full px-4 py-3 rounded-xl font-bold text-sm text-center text-white transition-all duration-300 hover:scale-105"
                  style={{
                    background: 'rgba(255, 255, 255, 0.1)',
                    border: '2px solid rgba(192, 192, 192, 0.3)',
                  }}
                >
                  Gratis AI-scan
                </Link>
                <Link
                  href="/contact"
                  className="block w-full px-4 py-3 rounded-xl font-bold text-sm text-center text-white transition-all duration-300 hover:scale-105"
                  style={{
                    background: 'linear-gradient(135deg, #FF6600, #FF8833)',
                  }}
                >
                  Demo boeken
                </Link>
              </>
            ) : (
              <>
                <Link
                  href="/contact"
                  className="flex items-center justify-center w-full p-3 rounded-xl transition-all duration-300 hover:scale-110"
                  style={{
                    background: 'rgba(255, 255, 255, 0.1)',
                    border: '2px solid rgba(192, 192, 192, 0.3)',
                  }}
                >
                  <Icon name="SparklesIcon" size={20} className="text-white" />
                </Link>
                <Link
                  href="/contact"
                  className="flex items-center justify-center w-full p-3 rounded-xl transition-all duration-300 hover:scale-110"
                  style={{
                    background: 'linear-gradient(135deg, #FF6600, #FF8833)',
                  }}
                >
                  <Icon name="CalendarIcon" size={20} className="text-white" />
                </Link>
              </>
            )}
          </div>
        </div>
      </aside>
    </>
  );
};

export default Header;